import { Injectable } from "@angular/core";
import { BehaviorSubject } from "rxjs";
import { ModalController, Platform, ToastController } from "@ionic/angular";
import { Network } from "@capacitor/network";
import { PluginListenerHandle } from "@capacitor/core";
import { AlertController } from "@ionic/angular/standalone";
import { Router } from "@angular/router";
import { Location } from "@angular/common";
import AppConstants from "../constants/app.constants";
import { StorageService } from "./storage.service";
interface OfflineFile {
  id?: any;
  fileName: string;
  fileUrl: string;
  size?: string;
  type?: string;
}

@Injectable({ providedIn: "root" })
export class NetworkService {
  public networkStatus = new BehaviorSubject<boolean>(true);
  private networkListenerHandle: PluginListenerHandle | null = null;
  private watcherInitPromise: Promise<void> | null = null;
  constructor(private toastCtrl: ToastController, private platform: Platform, private modalClt: ModalController, private alertCtrl: AlertController, private router: Router, private location: Location, private storageService: StorageService) {
  }

  public async initNetworkWatcher() {
    if (this.networkListenerHandle) {
      return;
    }

    if (this.watcherInitPromise) {
      return this.watcherInitPromise;
    }

    this.watcherInitPromise = this.initNetworkWatcherInternal();
    try {
      await this.watcherInitPromise;
    } finally {
      this.watcherInitPromise = null;
    }
  }

  private async initNetworkWatcherInternal(): Promise<void> {
    await this.platform.ready();

    const status = await Network.getStatus();
    this.setStatus(status.connected);

    this.networkListenerHandle = await Network.addListener("networkStatusChange", (status: any) => {
      const hasChanged = this.setStatus(status.connected);

      if (!hasChanged) {
        return;
      }

      // Handle UI updates only when connectivity actually changes.
      this.handleNetworkChange(status.connected);
    });
  }

  private setStatus(connected: boolean): boolean {
    if (this.networkStatus.value !== connected) {
      this.networkStatus.next(connected);
      return true;
    }

    return false;
  }

  getStatus(): boolean {
    return this.networkStatus.value;
  }

  get status$() {
    return this.networkStatus.asObservable();
  }

  async checkOnline(status: any, message: any, action: any): Promise<boolean> {
    const isOnline = await this.getStatus();
    if (!isOnline) {
      const alert = await this.alertCtrl.create({
        header: status,
        message: message,
        mode: 'md',
        buttons: [
          {
            text: action,
            handler: () => {
              this.router.navigate(['pages/my-alarm']);
            }
          }
        ],
        backdropDismiss: false
      });
      await alert.present();
      return false;
    }
    return true;
  }

  async checkOnlineDontNavigate(status: any, message: any, action: any): Promise<boolean> {
    const isOnline = await this.getStatus();
    if (!isOnline) {
      const alert = await this.alertCtrl.create({
        header: status,
        message: message,
        mode: 'md',
        buttons: [
          {
            text: action,
            handler: () => {
              this.location.back();
            }
          }
        ],
        backdropDismiss: false
      });
      await alert.present();
      return false;
    }
    return true;
  }

  async checkOnlineStay(status: any, message: any, action: any): Promise<boolean> {
    const isOnline = this.getStatus();
    if (!isOnline) {
      const alert = await this.alertCtrl.create({
        header: status,
        message: message,
        mode: 'md',
        buttons: [
          {
            text: action,
            handler: () => {
              this.modalClt.dismiss()
            }
          }
        ],
        backdropDismiss: false
      });
      await alert.present();
      return false;
    }
    return true;
  }

  private storageKey = AppConstants.OFFLINE_FILES;

  async getOfflineFiles(): Promise<any[]> {
    const stored = await this.storageService.getItem(this.storageKey);
    return stored ? JSON.parse(stored) : [];
  }

  private normalizeFile(fileItem: any): OfflineFile {
    const url =
      fileItem?.fileurl ||
      fileItem?.file_name ||
      fileItem?.filename ||
      fileItem?.urlLink ||
      fileItem?.filePath ||
      "";

    const name =
      fileItem?.file ||
      fileItem?.fileName ||
      fileItem?.document_name_encrpt ||
      fileItem?.filename_encrpt ||
      fileItem?.document_file ||
      "tempfile";

    return {
      id: fileItem?.id,
      fileName: name,
      fileUrl: url,
      size: fileItem?.file_size || fileItem?.size,
      type: fileItem?.type || "pdf",
    };
  }

  async markFileAsAvailable(fileItem: any): Promise<void> {
    const files = await this.getOfflineFiles();
    const normalized = this.normalizeFile(fileItem);

    if (!files.some(f => f.fileUrl === normalized.fileUrl)) {
      files.push(normalized);
      await this.storageService.setItem(this.storageKey, JSON.stringify(files));
    }
  }

  async isFileAvailableOffline(fileItem: any): Promise<boolean> {
    const files = await this.getOfflineFiles();
    const normalized = this.normalizeFile(fileItem);
    return files.some(f => f.fileUrl === normalized.fileUrl);
  }

  public isNfcScanning = false; // 🔥 ADD THIS

  private getCachedTranslation(key: string, fallback: string): string {
    const selectedLanguage = this.storageService.getItem(AppConstants.SELECTED_LANGUAGE) || AppConstants.DEFAULT_LANGUAGE;
    const cacheKey = `${AppConstants.TRANSLATION_PREFIX}${selectedLanguage}`;
    const raw = this.storageService.getItem(cacheKey);

    if (!raw) {
      return fallback;
    }

    try {
      const dictionary = JSON.parse(raw);
      const value = dictionary?.[key];
      return typeof value === "string" && value.trim() ? value : fallback;
    } catch {
      return fallback;
    }
  }

private offlineToast: HTMLIonToastElement | null = null;

  async handleNetworkChange(isConnected: boolean) {
    // 🚫 IGNORE during NFC scan
    if (this.isNfcScanning) {
      return;
    }

    if (isConnected) {
            if (this.offlineToast) {
        await this.offlineToast.dismiss();
        this.offlineToast = null;
      }
      // Optional: show back online message
      const onlineToast = await this.toastCtrl.create({
        message: this.getCachedTranslation('back_online', 'Back online'),
        duration: 1500,
        color: 'success',
        position: 'bottom'
      });

      await onlineToast.present();
    }  else {
      if (this.offlineToast) return;
      
      this.offlineToast = await this.toastCtrl.create({
        message: this.getCachedTranslation('no_internet_connection', 'No internet connection'),
        color: 'danger',
        position: 'bottom',
        buttons: [
          {
            text: this.getCachedTranslation('dismiss', 'Dismiss'),
            role: 'cancel' // optional
          }
        ]
      });
      await this.offlineToast.present();
    }
  }
}
